import { blockingFootprint, propsWithOwnFootprint } from "../compile/prop-footprint.ts";
import { initialDefaultActivation } from "../compile/behaviour.ts";
import type { CompileContext } from "../compile/context.ts";
import type { PropFootprint } from "../compile/footprint.ts";
import type { FrameDepth } from "../compile/ground-depth.ts";
import type { FrameMarks } from "../compile/stand-map.ts";
import type { Command } from "../document/commands.ts";
import { assertInstanceFootprint, canonicalCells, ownFootprintDrops } from "../document/footprint-cells.ts";
import { sameJson } from "../document/same-json.ts";
import type { InstanceFootprint, JsonValue, MapDocument, PropBehaviour, PropInstance, TileRef, WorldDocument } from "../document/types.ts";
import type { DaedalusGameAdapter } from "./adapter.ts";
import { behaviourFieldDefaults, furtherStates } from "./behaviour.ts";
import { frameNamed, type EditorCatalogue } from "./catalogue.ts";
import { pullPicksCommand } from "./packs.ts";
import type { ObjectTypeDefinition } from "./objects.ts";
import { addEntityCommand, addPropCommand, changePropCommand, movePropCommand, removeEntityCommand, removePropCommand, requireEntity, requireProp, setPropBehaviourCommand, updateEntityCommand } from "./panel-commands.ts";
import type { ReferenceThing } from "./references.ts";
import type { EntityTypeDefinition } from "./schema.ts";
import { suggestedAnimations } from "./suggest-animations.ts";
import type { PropRef } from "./templates.ts";

/**
 * Prop and marker authoring: how placement snaps (ADR-027), the footprints the compile context holds for frames
 * (ADR-040), placing, mirroring, offsetting, moving and removing props and markers, a prop's own footprint
 * (ADR-040 amendment of 2026-10-04) and its behaviour (ADR-053), each edit one command.
 */

/**
 * How the editor snaps prop placement and drags.
 */
export type SnapMode = "grid" | "pixel";

/**
 * What prop authoring needs from the store.
 */
export interface PropHost {
  readonly document: () => WorldDocument;
  readonly map: () => MapDocument;
  readonly context: CompileContext;
  readonly catalogue: EditorCatalogue;
  readonly adapter: DaedalusGameAdapter;
  readonly objectTypes: () => readonly ObjectTypeDefinition[];
  readonly entityTypes: () => readonly EntityTypeDefinition[];
  readonly requireEditable: (layerId: string) => void;
  readonly allocateId: (prefix: string) => string;
  readonly stepped: (prop: PropInstance, stepX: number, stepY: number) => { x: number; y: number };
  readonly select: (refs: readonly PropRef[]) => void;
  readonly guardDelete: (things: readonly ReferenceThing[], run: () => boolean) => boolean;
  readonly contextChanged: () => void;
  readonly repaintAll: () => void;
  readonly run: (command: Command) => boolean;
  readonly notify: () => void;
}

/**
 * What callers reach of prop and marker authoring, through `EditorStore.props`.
 */
export type PropSlice = Pick<PropAuthoring, "snapMode" | "setSnapMode" | "snapPosition" | "ruleFillsTile" | "footprintOf" | "derivedFootprintOf" | "setFootprint" | "depthOf" | "setDepth" | "marksOf" | "libraryMarksOf" | "previewMarks" | "dropMarksPreview" | "setMarks" | "add" | "stagePull" | "unstagePull" | "pullStaged" | "setOwnFootprint" | "blockingFootprint" | "withOwnFootprint" | "selectOwnFootprints" | "flip" | "offset" | "move" | "nudge" | "step" | "switchBehaviour" | "setBehaviour" | "suggestedStateFrame" | "defaultActivationCells" | "remove" | "addMarker" | "setMarkerFields" | "moveMarker" | "removeMarker">;

/**
 * The frames of one tileset a staged pull would add.
 */
export interface PullPick {
  readonly tileset: string;
  readonly frames: readonly number[];
}

/**
 * The key a frame is staged under.
 *
 * @param tileset Tileset id.
 * @param tile Local tile.
 * @returns The key.
 */
function stagedKey(tileset: string, tile: number): string {
  return `${tileset}\u0000${tile}`;
}

/**
 * The prop and marker author.
 */
export class PropAuthoring {
  private snap: SnapMode = "grid";
  private readonly staged = new Map<string, readonly PullPick[]>();
  private readonly previewed = new Map<string, FrameMarks | undefined>();

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: PropHost) {}

  /**
   * How placement and drags snap (ADR-027): to whole cells or to pixels. View state outside history.
   *
   * @returns The mode.
   */
  public get snapMode(): SnapMode {
    return this.snap;
  }

  /**
   * Sets the snap mode.
   *
   * @param mode New mode.
   */
  public setSnapMode(mode: SnapMode): void {
    this.snap = mode;
    this.host.notify();
  }

  /**
   * Snaps a tile position per the snap mode.
   *
   * @param x Position x in tiles.
   * @param y Position y in tiles.
   * @returns The snapped position: whole cells in `grid` mode, pixels in `pixel` mode.
   */
  public snapPosition(x: number, y: number): { x: number; y: number } {
    return this.snap === "grid" ? { x: Math.floor(x), y: Math.floor(y) } : { x: this.quantise(x), y: this.quantise(y) };
  }

  /**
   * Rounds a tile coordinate to the pixel lattice.
   *
   * @param value Tile coordinate.
   * @returns The nearest multiple of `1/tileSize`.
   */
  public quantise(value: number): number {
    const steps = this.host.document().tileSize;
    return Math.round(value * steps) / steps;
  }

  /**
   * Whether a tiles rule fills a frame's whole tile when it has no footprint (ADR-046): the collision a
   * painted tile gets by default.
   *
   * @param tileset Tileset id.
   * @param tile Local tile id.
   * @returns True when a tiles rule lists the tile.
   */
  public ruleFillsTile(tileset: string, tile: number): boolean {
    return this.host.context.collision.rules.some((rule) => rule.kind === "tiles" && rule.tileset === tileset && rule.tiles.includes(tile));
  }

  /**
   * The footprint the compile context holds for a prop frame (ADR-040).
   *
   * @param frameId Frame id (`<tileset>/default/<tile>`).
   * @returns The footprint, or undefined when the frame has none.
   */
  public footprintOf(frameId: string): PropFootprint | undefined {
    return this.host.context.collision.footprints?.[frameId];
  }

  /**
   * The footprint the build derived for a frame from its art (P1.6-T11): the context's record of what an
   * override replaced, else the footprint the frame holds, since nothing has replaced it.
   *
   * @param frameId Frame id.
   * @returns The derived footprint, or undefined when the game's policy gives the frame none.
   */
  public derivedFootprintOf(frameId: string): PropFootprint | undefined {
    const recorded = this.host.context.derivedFootprints;
    if (recorded !== undefined && Object.hasOwn(recorded, frameId)) {
      return recorded[frameId] ?? undefined;
    }
    return this.footprintOf(frameId);
  }

  /**
   * Replaces or removes a prop frame's footprint in the compile context so the overlay and the next
   * compile from this session see it; the caller persists the override. The first replacement records
   * the footprint the frame had as its derivation (`derivedFootprintOf`). Not part of history.
   *
   * @param frameId Frame id.
   * @param footprint The footprint, or null to make the frame walkable.
   */
  public setFootprint(frameId: string, footprint: PropFootprint | null): void {
    const context = this.host.context;
    const recorded = context.derivedFootprints ?? {};
    if (!Object.hasOwn(recorded, frameId)) {
      (context as { derivedFootprints?: Readonly<Record<string, PropFootprint | null>> }).derivedFootprints = { ...recorded, [frameId]: this.footprintOf(frameId) ?? null };
    }
    const footprints = context.collision.footprints as Record<string, PropFootprint> | undefined;
    if (footprints === undefined) {
      (context.collision as { footprints?: Record<string, PropFootprint> }).footprints = footprint === null ? {} : { [frameId]: footprint };
    } else if (footprint === null) {
      delete footprints[frameId];
    } else {
      footprints[frameId] = footprint;
    }
    this.host.contextChanged();
    this.host.notify();
    this.host.repaintAll();
  }

  /**
   * The depth the library holds for a frame (ADR-098 decision 19), as the loader read it and writes since have
   * patched it.
   *
   * @param frameId Frame id.
   * @returns The depth, or undefined when the frame has none.
   */
  public depthOf(frameId: string): FrameDepth | undefined {
    return this.host.context.savedDepths?.[frameId];
  }

  /**
   * Replaces or removes the depth the editor holds for a frame, once the library holds the write. View state of
   * the editor: no command, no history, and the world does not read unsaved. The record is replaced, never
   * changed in place, so a reader holding the old one is not surprised.
   *
   * @param frameId Frame id.
   * @param depth The depth, or null to take it away.
   */
  public setDepth(frameId: string, depth: FrameDepth | null): void {
    const context = this.host.context;
    const rest = Object.fromEntries(Object.entries(context.savedDepths ?? {}).filter(([id]) => id !== frameId));
    (context as { savedDepths?: Readonly<Record<string, FrameDepth>> }).savedDepths = depth === null ? rest : { ...rest, [frameId]: depth };
    this.host.notify();
  }

  /**
   * The Behind and In front cells the library holds for a frame (ADR-132 decision 2), as the loader read them and
   * writes since have patched them.
   *
   * @param frameId Frame id.
   * @returns The marks and the cell they are counted in, or undefined when the frame has none.
   */
  public marksOf(frameId: string): FrameMarks | undefined {
    return this.host.context.standMarks?.[frameId];
  }

  /**
   * The marks the library holds for a frame, apart from any the footprint panel is previewing: what a draft is compared
   * with and what a kept draft's base reads.
   *
   * @param frameId Frame id.
   * @returns The library's marks, or undefined when it holds none.
   */
  public libraryMarksOf(frameId: string): FrameMarks | undefined {
    return this.previewed.has(frameId) ? this.previewed.get(frameId) : this.marksOf(frameId);
  }

  /**
   * Shows marks that are not written yet as the frame's, so everything that reads `marksOf` (the stand-in on the map)
   * answers for the ground as the panel paints it. The library's own marks are kept for `libraryMarksOf` and for
   * `dropMarksPreview`. View state: no command, no history, and the world does not read unsaved.
   *
   * @param frameId Frame id.
   * @param marks The marks as painted, or null for none.
   */
  public previewMarks(frameId: string, marks: FrameMarks | null): void {
    if (!this.previewed.has(frameId)) {
      this.previewed.set(frameId, this.marksOf(frameId));
    }
    this.replaceMarks(frameId, marks);
  }

  /**
   * Ends a preview: the library's marks are the frame's again.
   *
   * @param frameId Frame id.
   */
  public dropMarksPreview(frameId: string): void {
    if (this.previewed.has(frameId)) {
      const held = this.previewed.get(frameId);
      this.previewed.delete(frameId);
      this.replaceMarks(frameId, held ?? null);
    }
  }

  /**
   * Replaces or removes the marks the editor holds for a frame, once the library holds the write. View state of the
   * editor, as `setDepth` was: no command, no history, and the world does not read unsaved. The record is replaced,
   * never changed in place.
   *
   * @param frameId Frame id.
   * @param marks The marks, or null to take them away.
   */
  public setMarks(frameId: string, marks: FrameMarks | null): void {
    this.previewed.delete(frameId);
    this.replaceMarks(frameId, marks);
  }

  /**
   * Puts marks into the context, replacing the record, and tells the editor.
   *
   * @param frameId Frame id.
   * @param marks The marks, or null to take them away.
   */
  private replaceMarks(frameId: string, marks: FrameMarks | null): void {
    const context = this.host.context;
    const rest = Object.fromEntries(Object.entries(context.standMarks ?? {}).filter(([id]) => id !== frameId));
    (context as { standMarks?: Readonly<Record<string, FrameMarks>> }).standMarks = marks === null ? rest : { ...rest, [frameId]: marks };
    this.host.notify();
  }

  /**
   * Stages the pull a frame taken in hand needs (ADR-129's amendment A3, ADR-130 decision 3.1): nothing changes in
   * the document until the frame is placed, and then the placement carries the pull with it when the world does not
   * offer the frame (the first one, and again after an undo takes it back). The pull is
   * checked against the caps now, so a hand never holds a frame its placement would refuse.
   *
   * @param tileset The frame's tileset id.
   * @param tile The frame's local tile.
   * @param picks The tilesets and local tile ids the placement adds to the world (the frame and what it brings along).
   * @returns Whether placing the frame will add anything: false when the world already offers every pick.
   * @throws Error with the cap's sentence when the pull would pass a cap of ADR-129 decision 6; nothing is staged then.
   */
  public stagePull(tileset: string, tile: number, picks: readonly PullPick[]): boolean {
    const command = pullPicksCommand(this.host.document(), picks);
    if (command === null) {
      this.staged.delete(stagedKey(tileset, tile));
      return false;
    }
    this.staged.set(stagedKey(tileset, tile), picks);
    return true;
  }

  /**
   * Whether a pull is staged for a frame, which is the one case where a frame in the hand may name a tileset the
   * world does not list yet.
   *
   * @param tileset The frame's tileset id.
   * @param tile The frame's local tile.
   * @returns True while the frame's first placement carries a pull.
   */
  public pullStaged(tileset: string, tile: number): boolean {
    return this.staged.has(stagedKey(tileset, tile));
  }

  /**
   * Forgets the pull staged for a frame, as a hand that lets go of it does.
   *
   * @param tileset The frame's tileset id.
   * @param tile The frame's local tile.
   */
  public unstagePull(tileset: string, tile: number): void {
    this.staged.delete(stagedKey(tileset, tile));
  }

  /**
   * Places a prop as one command. A behaviour given is placed on it as the data it is, whatever its type, so
   * a copy of a configured prop carries its type, fields, state frames, animations and activation cells.
   *
   * @param layerId Prop layer id, which must not be locked.
   * @param tileset Frame tileset id.
   * @param tile Frame local tile.
   * @param x Anchor x in tiles, rounded to the pixel lattice.
   * @param y Anchor y in tiles, rounded to the pixel lattice.
   * @param flipX Mirror horizontally.
   * @param flipY Mirror vertically.
   * @param sortPixels Whole pixels added to the line the prop sorts by, positive in front.
   * @param behaviour The behaviour the prop carries (ADR-053), or undefined for none.
   * @param footprint The prop's own footprint (ADR-040 amendment of 2026-10-04), as a copy of a prop that has
   *   one carries it, or undefined for the frame's.
   * @returns The new prop's id.
   * @throws Error when the layer is locked or hidden, a footprint comes with a behaviour, or a pull staged for the
   *   frame would now pass a cap; nothing is placed then.
   */
  public add(layerId: string, tileset: string, tile: number, x: number, y: number, flipX = false, flipY = false, sortPixels = 0, behaviour?: PropBehaviour, footprint?: InstanceFootprint): string {
    this.host.requireEditable(layerId);
    const id = this.host.allocateId("prop");
    const placing = addPropCommand(this.host.map(), layerId, { id, frame: { tileset, tile }, x: this.quantise(x), y: this.quantise(y), flipX, flipY, sortPixels, ...(behaviour === undefined ? {} : { behaviour }), ...(footprint === undefined ? {} : { footprint }) });
    const key = stagedKey(tileset, tile);
    const picks = this.staged.get(key);
    const pull = picks === undefined ? null : pullPicksCommand(this.host.document(), picks);
    this.host.run(pull === null ? placing : { kind: "compound", commands: [pull, placing] });
    return id;
  }

  /**
   * Gives a placed prop a footprint of its own, or takes it away, as one command (ADR-040 amendment of
   * 2026-10-04): the cells replace the frame's shared footprint for this one prop, in the document, so the
   * world reads unsaved, undo takes it back and every reader of the prop's footprint follows. The cells are put
   * in canonical order. Setting what the prop already holds records nothing.
   *
   * @param layerId Prop layer id, which must not be locked or hidden.
   * @param propId Prop id.
   * @param cells `[column, rowsUp]` cells from the frame's bottom-left, empty to block nothing, or null to go
   *   back to the frame's shared footprint.
   * @param marks The prop's own Behind and In front cells (ADR-132 decision 12), each put in canonical order. A list
   *   given is written, an empty one meaning the prop has none of that mark; a list left out is not written, so the
   *   prop inherits its frame's. Ignored when `cells` is null.
   * @throws Error when the layer is locked or hidden, the prop does not exist or carries a behaviour, or the
   *   cells are out of bounds.
   */
  public setOwnFootprint(layerId: string, propId: string, cells: readonly (readonly [number, number])[] | null, marks?: { readonly behind?: readonly (readonly [number, number])[]; readonly front?: readonly (readonly [number, number])[] }): void {
    this.host.requireEditable(layerId);
    const prop = this.find(layerId, propId);
    if (prop === undefined) {
      throw new Error(`prop ${propId} does not exist in layer ${layerId}`);
    }
    const footprint: InstanceFootprint | undefined = cells === null ? undefined : { cells: canonicalCells(cells), ...(marks?.behind === undefined ? {} : { behind: canonicalCells(marks.behind) }), ...(marks?.front === undefined ? {} : { front: canonicalCells(marks.front) }) };
    assertInstanceFootprint({ ...(footprint === undefined ? {} : { footprint }), ...(prop.behaviour === undefined ? {} : { behaviour: prop.behaviour }) }, propId);
    if (sameJson(prop.footprint, footprint)) {
      return;
    }
    this.host.run(changePropCommand(this.host.map(), layerId, propId, { footprint }));
  }

  /**
   * The footprint a placed prop blocks by, as the compile and the overlay resolve it (`blockingFootprint`):
   * its own cells when it carries them, else its frame's.
   *
   * @param prop The prop.
   * @returns The footprint, or undefined when the prop has none.
   */
  public blockingFootprint(prop: PropInstance): PropFootprint | undefined {
    return blockingFootprint(prop, this.host.context, this.host.document().tileSize);
  }

  /**
   * The props of the map in view that carry a footprint of their own, in document order.
   *
   * @returns The props.
   */
  public withOwnFootprint(): readonly PropRef[] {
    return propsWithOwnFootprint(this.host.map());
  }

  /**
   * Gathers every prop of the map in view that carries a footprint of its own, so the author can find them;
   * a prop on a locked or hidden layer cannot be gathered and is left out.
   *
   * @returns How many props the map holds with a footprint of their own, gathered or not.
   */
  public selectOwnFootprints(): number {
    const refs = this.withOwnFootprint();
    this.host.select(refs);
    return refs.length;
  }

  /**
   * Mirrors a prop on one axis as one command.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @param axis Axis to mirror.
   */
  public flip(layerId: string, propId: string, axis: "x" | "y"): void {
    const prop = this.require(layerId, propId);
    this.host.run(changePropCommand(this.host.map(), layerId, propId, axis === "x" ? { flipX: !prop.flipX } : { flipY: !prop.flipY }));
  }

  /**
   * Shifts a prop's sort offset as one command.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @param delta Whole pixels to add.
   */
  public offset(layerId: string, propId: string, delta: number): void {
    const prop = this.require(layerId, propId);
    this.host.run(changePropCommand(this.host.map(), layerId, propId, { sortPixels: prop.sortPixels + delta }));
  }

  /**
   * Moves a prop as one command.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @param x New anchor x in tiles, rounded to the pixel lattice.
   * @param y New anchor y in tiles, rounded to the pixel lattice.
   * @returns False when the layer's lock or its hiding refused the move, which then records nothing.
   */
  public move(layerId: string, propId: string, x: number, y: number): boolean {
    return this.host.run(movePropCommand(this.host.map(), layerId, propId, this.quantise(x), this.quantise(y)));
  }

  /**
   * Moves a prop by whole pixels as one command.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @param dx Pixels right.
   * @param dy Pixels down.
   * @returns False when the layer's lock or its hiding refused the move, which then records nothing.
   * @throws Error when the prop is missing.
   */
  public nudge(layerId: string, propId: string, dx: number, dy: number): boolean {
    const prop = this.require(layerId, propId);
    const steps = this.host.document().tileSize;
    return this.move(layerId, propId, prop.x + dx / steps, prop.y + dy / steps);
  }

  /**
   * Moves a prop by collision cells as one command (ADR-040 amendment of 2026-10-03, "props stand on the
   * cells"): on each axis stepped its frame's corner goes to the next cell edge that way (`cellStepped`), so a
   * prop on the cells stays on them and one between them lands on them.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @param stepX Cells right; negative steps left.
   * @param stepY Cells down; negative steps up.
   * @returns False when the layer's lock or its hiding refused the move, which then records nothing.
   * @throws Error when the prop is missing.
   */
  public step(layerId: string, propId: string, stepX: number, stepY: number): boolean {
    const to = this.host.stepped(this.require(layerId, propId), stepX, stepY);
    return this.move(layerId, propId, to.x, to.y);
  }

  /**
   * Removes a prop, or holds the removal for its question while anything else references it (P1.6-T35).
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   */
  public remove(layerId: string, propId: string): void {
    this.host.guardDelete([{ kind: "prop", layerId, propId }], () => this.host.run(removePropCommand(this.host.map(), layerId, propId)));
  }

  /**
   * Switches a behaviour on or off for a placed prop as one command (ADR-053). Switching one on starts from
   * the type's schema defaults and, for every state past the initial one, the frame the game suggests from
   * the placed frame's name resolved against the catalogue; switching to another type starts over on the
   * fields and the state frames, since those name states the new type does not have. Painted activation
   * cells survive the switch: they describe where the art is reachable from, which no type owns, and the
   * panel's own control clears them in one click when they are wrong.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @param type The object type to switch on, or null to switch behaviour off.
   * @returns 1 when switching the behaviour on dropped the prop's own footprint, else 0.
   * @throws Error when the prop does not exist or the type is not registered.
   */
  public switchBehaviour(layerId: string, propId: string, type: string | null): number {
    if (type === null) {
      return this.setBehaviour(layerId, propId, undefined);
    }
    const definition = this.host.objectTypes().find((t) => t.type === type);
    if (definition === undefined) {
      throw new Error(`object type ${type} is not registered`);
    }
    const { prop } = requireProp(this.host.map(), layerId, propId);
    return this.setBehaviour(layerId, propId, this.switchedOn(definition, prop));
  }

  /**
   * The behaviour a prop takes when a type is switched on for it: the type's field defaults, the game's
   * suggested frame for each further state and its suggested animations from the prop's own frame, and the
   * prop's painted activation cells kept.
   *
   * @param definition The registered type.
   * @param prop The prop.
   * @returns The behaviour.
   */
  public switchedOn(definition: ObjectTypeDefinition, prop: PropInstance): PropBehaviour {
    const frames: Record<string, TileRef> = {};
    for (const state of furtherStates(definition)) {
      const suggested = this.suggestedStateFrame(definition.type, state.id, prop.frame);
      if (suggested !== undefined) {
        frames[state.id] = suggested;
      }
    }
    const animations = suggestedAnimations(definition, prop.frame, this.host.catalogue, this.host.adapter.suggestAnimation);
    const activation = prop.behaviour?.activation;
    return { type: definition.type, fields: behaviourFieldDefaults(definition), ...(Object.keys(frames).length === 0 ? {} : { frames }), ...(Object.keys(animations).length === 0 ? {} : { animations }), ...(activation === undefined ? {} : { activation }) };
  }

  /**
   * Replaces a prop's behaviour as one command, or removes it when none is given (ADR-053). Every edit in the
   * inspector, a field, a state's frame or the activation cells, is one of these.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @param behaviour The behaviour, or undefined for none.
   * @returns 1 when the command dropped the prop's own footprint, which a prop with a behaviour cannot keep
   *   (`ownFootprintDrops`), so the caller can say so; else 0.
   * @throws Error when the prop does not exist or a state frame names an unknown tileset.
   */
  public setBehaviour(layerId: string, propId: string, behaviour: PropBehaviour | undefined): number {
    const command = setPropBehaviourCommand(this.host.map(), layerId, propId, behaviour);
    return this.host.run(command) ? ownFootprintDrops(command) : 0;
  }

  /**
   * The frame the game suggests for a behaviour's further state, resolved against the catalogue (ADR-053
   * decision 2). The game names a frame; Daedalus finds it, preferring the sheet the placed frame came from.
   *
   * @param type The object type id.
   * @param stateId The state wanting a frame.
   * @param placed The frame the prop carries.
   * @returns The suggested frame, or undefined when the game suggests none or the catalogue has no such frame.
   */
  public suggestedStateFrame(type: string, stateId: string, placed: TileRef): TileRef | undefined {
    const catalogue = this.host.catalogue;
    const name = catalogue.props.find((f) => f.tileset === placed.tileset && f.tile === placed.tile)?.name;
    const suggestion = name === undefined ? undefined : this.host.adapter.suggestFrame?.(type, stateId, name);
    return suggestion === undefined ? undefined : frameNamed(catalogue, suggestion, placed.tileset);
  }

  /**
   * The activation cells a behaviour prop falls back to when its behaviour names none (ADR-053 decision 8):
   * the ring the compiler grows around the initial state's footprint, in the frame's own unmirrored space, so
   * the inspector can show what the default covers before the author paints over it.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @returns The cells, empty when the prop carries no behaviour or its initial state stamps nothing.
   * @throws Error when the prop does not exist.
   */
  public defaultActivationCells(layerId: string, propId: string): readonly (readonly [number, number])[] {
    const { prop } = requireProp(this.host.map(), layerId, propId);
    return initialDefaultActivation(prop, this.host.context, this.host.document().tileSize);
  }

  /**
   * Adds an entity with the type's schema defaults.
   *
   * @param layerId Entity layer id.
   * @param type Entity type.
   * @param x Position x in tiles.
   * @param y Position y in tiles.
   * @returns The new entity's id.
   * @throws Error when the type is not registered, the layer is not an entity layer, or it is locked or hidden.
   */
  public addMarker(layerId: string, type: string, x: number, y: number): string {
    this.host.requireEditable(layerId);
    const definition = this.host.entityTypes().find((t) => t.type === type);
    if (definition === undefined) {
      throw new Error(`entity type ${type} is not registered`);
    }
    const id = this.host.allocateId("entity");
    this.host.run(addEntityCommand(this.host.map(), layerId, definition, id, x, y));
    return id;
  }

  /**
   * Replaces an entity's fields as one command.
   *
   * @param layerId Entity layer id.
   * @param entityId Entity id.
   * @param fields New fields.
   */
  public setMarkerFields(layerId: string, entityId: string, fields: { readonly [key: string]: JsonValue }): void {
    const map = this.host.map();
    const entity = requireEntity(map, layerId, entityId);
    this.host.run(updateEntityCommand(map, layerId, entityId, { x: entity.x, y: entity.y, fields }));
  }

  /**
   * Moves an entity.
   *
   * @param layerId Entity layer id.
   * @param entityId Entity id.
   * @param x New x.
   * @param y New y.
   */
  public moveMarker(layerId: string, entityId: string, x: number, y: number): void {
    const map = this.host.map();
    const entity = requireEntity(map, layerId, entityId);
    this.host.run(updateEntityCommand(map, layerId, entityId, { x, y, fields: entity.fields }));
  }

  /**
   * Removes an entity, or holds the removal for its question while anything else references it (P1.6-T35).
   *
   * @param layerId Entity layer id.
   * @param entityId Entity id.
   */
  public removeMarker(layerId: string, entityId: string): void {
    this.host.guardDelete([{ kind: "marker", layerId, entityId }], () => this.host.run(removeEntityCommand(this.host.map(), layerId, entityId)));
  }

  /**
   * A prop of the map in view.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @returns The prop, or undefined when the layer is missing, holds no props or lacks it.
   */
  private find(layerId: string, propId: string): PropInstance | undefined {
    const layer = this.host.map().layers.find((l) => l.id === layerId);
    return layer?.kind === "prop" ? layer.props.find((p) => p.id === propId) : undefined;
  }

  /**
   * A prop of the map in view that must be there.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @returns The prop.
   * @throws Error when it is not.
   */
  private require(layerId: string, propId: string): PropInstance {
    const prop = this.find(layerId, propId);
    if (prop === undefined) {
      throw new Error(`prop ${propId} does not exist in layer ${layerId}`);
    }
    return prop;
  }
}
